import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from "@angular/forms";
import { ActivatedRoute, Router} from "@angular/router";
import { Subscription, Observable, of } from "rxjs";
import { mergeMap, tap } from "rxjs/operators";
import { WorkOrder, WorkOrderStatus } from "../../../../common/models/work-order.model";
import { FlowIndex } from "../../../../common/models/flow.dto";
import { RequestForScanning, requireOneOfGroup, ScanningSteps } from "../../../../common/models/scan-documents.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { ScanRequestService } from "../../../services/scan-request.service";
import { OrganizationService } from "../../../../common/services/organization.service";
import { GroupService, GroupSearchSpec } from "../../../services/group.service";
import { AuthService } from "../../../../services/auth.service";
import { LunaClaimTypes, LunaClaimValues } from "../../../../models/user-identity.model";
import { Group } from "../../../models/group.model";
import { Department, Organization, OrganizationStub } from "../../../../common/models/organization.model";
import { OnBaseIntegrationAndMappingService } from "../../../../common/services/onbase-integration-mapping.service";
import { OnBaseDocumentType, OnBaseDocumentGroup } from '../../../../common/models/onBaseIntegrationAndMapping.model';
import { DocumentManagementService, IUploadFile } from "../../../../common/services/document-management.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { deepClone } from "../../../../builder/models/editor.model";
import { WellService } from "../../../../common/services/well.service";
import { FlowIndexSpec, FlowInstanceService } from "../../../../flow/services/flow-instance.service";
import { UploadViewFilesComponent } from '../../../../common/components/fileUpload/upload-view-files.component';
import { CanDeactivateComponent } from "../../../../common/classes/can-deactivate.component";
import { WorkOrderService } from "../../../../common/services/work-order.service";



@Component({
    selector: 'app-submit-scan',
    templateUrl: './submit-scan.component.html',
    styleUrls: ['./submit-scan.component.scss', '../request-scan/request-scan.component.scss'],
    standalone: false
})
export class SubmitScanComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    allDocumentTypes: OnBaseDocumentType[] = [];
    alreadyCompleteDialog: Dialog = new Dialog("This work order has already been completed.", [new DialogButton("Exit", false, () => { this.requestCompleted(); })]);

    apiSearchRun: boolean = false;

    get apiValid(): boolean {
        let valid = true;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("apiNumber");
            valid = ctrl && (ctrl.disabled || ctrl.value);
            let found = this.formGroup.get("apiFound");
            valid = valid && (!found || found.value);
        }
        return valid;
    }

    applicationSearchRun: boolean = false;

    get applicationValid(): boolean {
        let valid = true;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("applicationNumber");
            valid = ctrl && (ctrl.disabled || ctrl.value);
            let found = this.formGroup.get("applicationNumberFound");
            valid = valid && (!found || found.value);
        }
        return valid;
    }

    get completeButtonText(): string {
        return (this.currentStep === ScanningSteps.Rejected) ? "Resubmit" : "Complete";
    }
    confirmDeleteDialog: Dialog = new Dialog("The selected document will be permanently deleted.  If the replacement scan has not already been uploaded, you will need to upload a new document before being able to proceed.  Are you sure you wish to delete this document", [new DialogButton("Cancel", false, () => { this.cancelDelete(); }), new DialogButton("Confirm", true, () => { this.confirmDelete(); })])
    confirmLegalDialog: Dialog = new Dialog("You are calling the legal review complete.  Please be sure any comments have been recorded.  You will not get an opportunity to update this form later unless the request is resubmitted for legal review.", [new DialogButton("Cancel", false, () => { return; }), new DialogButton("OK", true, () => { this.confirmLegal(); })]);
  

    currentRequest: RequestForScanning;
    currentStep: ScanningSteps = ScanningSteps.Loading;

    get dateInvalid(): boolean {
        let invalid: boolean = false;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("receivedDate");
            invalid = ctrl && ctrl.invalid;
        }
        return invalid;
    }

    departments: Department[];

    documentTypes: OnBaseDocumentType[];

    @ViewChild("downloadLink", { static: true }) download: ElementRef;
    enableButtons: boolean = false;
    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);

    get files(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.currentRequest) {
            if (this.currentRequest.files == null) {
                this.currentRequest.files = [];
            }
            files = this.currentRequest.files;
        }
        return files;
    }

    @ViewChild(UploadViewFilesComponent) public filesComponent: UploadViewFilesComponent;

    formGroup: FormGroup;

    get groupInvalid(): boolean {
        let invalid: boolean = false;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("group");
            invalid = ctrl && ctrl.invalid;
        }
        return invalid;
    }


    groups: Group[];

    get hasSearchOption(): boolean {
        return (this.apiValid || this.applicationValid || this.organizationValid);
    }

    index: FlowIndex;

    get inScanningGroup(): boolean {
        return this.currentStep === ScanningSteps.InitialScan || this.currentStep === ScanningSteps.ReturnedToScanning || this.currentStep === ScanningSteps.Rejected;
    }

    isEditing: boolean = false;

    legalCompleteDialog: Dialog = new Dialog("Legal Review of #[RequestID] has been completed", [new DialogButton("OK", false, () => { this.requestCompleted(); })]);

    get legalGroup(): Group | undefined {
        let legal = this.groups.find(g => g.displayName.toLocaleLowerCase().indexOf("legal") !== 0);
        return legal;
    }

    get legalReview(): boolean {
        let result: boolean = false;
        let ctrl = this.formGroup.get("legalReview");
        if (ctrl) {
            result = <boolean>ctrl.value;
        }
        return result;
    }

    noFilesDialog: Dialog = new Dialog("You have not uploaded any files to the request.", [new DialogButton("Cancel", false, () => { return; })]);
    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => { return; })]);
    organization: OrganizationStub;

    originalRequest: RequestForScanning;

    organizationSearchRun: boolean = false;

    get organizationValid(): boolean {
        let valid = true;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("organizationNumber");
            valid = ctrl && (ctrl.disabled || ctrl.value);
            let found = this.formGroup.get("organizationNumberFound");
            valid = valid && (!found || found.value);
        }
        return valid;
    }

    documentGroupDocumentTypeMap: Map<number, OnBaseDocumentType[]> = new Map<number, OnBaseDocumentType[]>();
    documentGroups: OnBaseDocumentGroup[];
    pendingDeleteFile: IUploadFile;
    rbdmsNumberSearchRun: boolean = false;

    get rbdmsValid(): boolean {
        let valid = true;
        if (this.formGroup) {
            let ctrl = this.formGroup.get("rbdmsNumber");
            valid = ctrl && (ctrl.disabled || ctrl.value);
            let found = this.formGroup.get("rbdmsNumberFound");
            valid = valid && (!found || found.value);
        }
        return valid;
    }

    get receivedDate(): Date {
        if (this.currentRequest.dateDocumentReceived) {
            return new Date(this.currentRequest.dateDocumentReceived);
        }
        return new Date();
    }

    get requestId(): string {
        let id: string = "";
        if (this.requestWorkOrder) {
            id = this.requestWorkOrder.applicationNumber;
        }
        return id;
    }

    requestWorkOrder: WorkOrder;

    searchSaveDialog: Dialog = new Dialog("When updating lookup values, only those values with a successfully completed search will be updated", [new DialogButton("OK", true, () => { return; })]);
    selectDepartment = { id: "", displayName: "--Select Group--" } as Department;
    selectDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };

    get currentDocumentGroup(): OnBaseDocumentGroup {
        if (this.currentRequest == null || this.documentGroups == null) return this.selectDocumentGroup;
        let group = this.documentGroups.find(g => g.name === this.currentRequest.documentGroup);
        if (group == null) return this.selectDocumentGroup;
        return group;
    }

    get currentDocumentType(): OnBaseDocumentType {
        if (this.currentRequest == null || this.allDocumentTypes == null) return this.selectDocumentType;
        let type = this.allDocumentTypes.find(g => g.name === this.currentRequest.documentType);
        if (type == null) return this.selectDocumentType;
        return type;
    }

    selectGroup = { id: "", displayName: "--Select Group--", permissions: [] } as Group;
    selectDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    sentToLegalDialog: Dialog = new Dialog("Request #[RequestID] has been sent for legal review.", [new DialogButton("OK", true, () => { this.requestCompleted(); })]);
    sentToDocumentGroupDialog: Dialog = new Dialog("Request #[RequestID] has been sent to document group for confirmation.", [new DialogButton("OK", true, () => { this.requestCompleted(); })]);

    steps = ScanningSteps;

    subscription: Subscription = new Subscription();

    constructor(private builder: FormBuilder, private route: ActivatedRoute, public router: Router, public _dialogService: DynamicDialogService, private _organizationService: OrganizationService,
        private _groupService: GroupService, private _subheaderSvc: SubHeaderService, private _requestService: ScanRequestService, auth: AuthService, 
        private _mappingService: OnBaseIntegrationAndMappingService, private readonly _documentService: DocumentManagementService, public readonly loader: LoaderService,
        private readonly _wellService: WellService, private readonly _instanceService: FlowInstanceService,
        private readonly _workOrderService: WorkOrderService) {
        super(_dialogService, auth);
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("<p class=\"bold\">You have made unsaved changes to the current request.  Are you sure you want to exit without saving?</p>", [new DialogButton("Exit Page", true, () => this.onLeaveConfirm()), new DialogButton("Cancel", false, () => { return;})])
        this.buildForm();
        const routeSubs$ = this.route.data.pipe(
            tap((data) => {
                this.loader.toggleLoader(false);
                if (data.workOrder) {
                    this.requestWorkOrder = data.workOrder;
                    this.originalRequest = this.requestWorkOrder.scanRequest;
                    this.currentRequest = deepClone(this.requestWorkOrder.scanRequest);
                }
                this._subheaderSvc.updateSubHeader({ title: `Document Scanning Request ${this.requestId}`, buttons: [{ name: "Download Original Cover Sheet", method: () => { this.downloadCoversheet(); } }] });
            }),
            mergeMap(() => {
                return this._documentService.getAllDocumentGroups().pipe(tap((groups: OnBaseDocumentGroup[]) => {
                    this.documentGroups = groups;
                }));
            }),
            mergeMap(() => {
                return this._documentService.getAllDocumentTypes().pipe(tap((types: OnBaseDocumentType[]) => {
                    this.allDocumentTypes = types;
                }));
            }),
            mergeMap(() => {
                return this._documentService.getDocumentTypesByDocumentGroupName(this.currentRequest.documentGroup).pipe(tap((types: OnBaseDocumentType[]) => {
                    this.documentGroupDocumentTypeMap.set(this.currentDocumentGroup.id, types);
                    this.documentTypes = types;
                    this.documentTypes.unshift(this.selectDocumentType);
                }));
            }),
            mergeMap(() => {
                return this._requestService.getScanRequest(this.requestId);
            })
        ).subscribe(
            (flowIndex: FlowIndex) => {
                this.index = flowIndex;    
                if (this.requestWorkOrder.status === WorkOrderStatus.Completed || this.requestWorkOrder.status === WorkOrderStatus.Cancelled) {
                    this.workOrderCompleted();
                    return;
                }
                if (this.index.state === ScanningSteps.DocumentGroup) {
                    this.allowDeactivate.next(true);
                    this.router.navigate(["/admin/scanning/confirm", this.requestWorkOrder.id]);
                }
                this.currentStep = <ScanningSteps>flowIndex.state;
                this.initializeForm();
                this.enableButtons = true;
            },
            (error) => {
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(routeSubs$);
        let orgObs$ = this._organizationService.getOrganization(this.auth.User.organizationId).pipe(tap((org: Organization) => {
            this.departments = org.departments.sort((a, b) => {
                if (a.displayName > b.displayName) return 1;
                if (a.displayName < b.displayName) return -1;
                return 0;
            });
            this.departments.unshift(this.selectDepartment);
        })).subscribe(() => { },
            (error) => {
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(orgObs$);
        let groupObs$ = this._groupService.get({ internal: true, stubs: false } as GroupSearchSpec).pipe(tap((groupData) => {
            let scanEligible: Group[] = (groupData as Group[]).filter(g => g.permissions.find(c => c.type === LunaClaimTypes.Widget && c.value === LunaClaimValues.ViewScanningWidget) != null);
            this.groups = scanEligible;
            this.groups.unshift(this.selectGroup);
        })).subscribe(() => {
        },
            (error) => {
                console.error(error);
                this.handleError(error);
            });
        this.subscription.add(groupObs$);

    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }

    appendFiles(file: IUploadFile): void {
        if (this.currentRequest.files == null) {
            this.currentRequest.files = [];
        }
        this.currentRequest.files.push(file);
        this.allowDeactivate.next(false);
        this.requestWorkOrder.scanRequest = this.currentRequest;
        this.loader.toggleLoader(true);
        this._workOrderService.save(this.requestWorkOrder).subscribe(
            (workOrder: WorkOrder) => {
                this.requestWorkOrder = workOrder;
                this.currentRequest = deepClone(workOrder.scanRequest);
                this.loader.toggleLoader(false);
            },
            (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.handleError(error);
            }
        );        
    }

    buildForm(): void {
        this.formGroup = this.builder.group({
            receivedDate: ["", [Validators.required]],
            group: ["", [Validators.required]],
            requestor: ["", [Validators.required]],
            documentGroup: [-1, [Validators.required, Validators.min(0)]],
            documentType: [-1, [Validators.required, Validators.min(0)]],
            apiNumber: ["", []],
            apiFound: [false, []],
            applicationNumber: ["", []],
            applicationNumberFound: [false, []],
            organizationNumber: ["", []],
            organizationNumberFound: [false, []],
            rbdmsNumber: ["", []],
            rbdmsNumberFound: [false, []],
            confidential: [false, []],
            legalReview: [false, []],
            pages: [0, [Validators.required, Validators.min(0)]],
            instructions: ["", []],
            scanningComments: ["", []],
            legalComments: ["", []],
            rejectReason: ["", []]
        }, { validators: [requireOneOfGroup(["apiFound", "applicationNumberFound", "organizationNumberFound", "rbdmsNumberFound"]), requireOneOfGroup(["applicationNumber", "organizationNumber", "apiNumber", "rbdmsNumber"])] });
        

        this.formGroup.disable();
    }

    completeLegalReview(): void {
        this.confirmLegalDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.confirmLegalDialog);
    }

    completeScan(): void {
        if (this.confirmFiles()) {
            this.save(true);
            this.loader.toggleLoader(true);
            let save$ = this._requestService.updateScanRequest("Program", this.requestWorkOrder.id, this.currentRequest).pipe()
                .subscribe(
                    (order: WorkOrder) => {
                        this.loader.toggleLoader(false);
                        this.sentToDocumentGroupDialog.message = this.sentToDocumentGroupDialog.message.replace("[RequestID]", this.index.flowIdentifier);
                        this.sentToDocumentGroupDialog.toggleDialog(true);
                        this._dialogService.updateDialog(this.sentToDocumentGroupDialog);
                    },
                    (error) => {
                        console.error(error);
                        this.loader.toggleLoader(false);
                        this.handleError(error);
                    }
            );
            this.subscription.add(save$);
        }
    }

    confirmFiles(): boolean {
        if (this.files != null && this.files.length > 0) {
            return true;
        }
        this.noFilesDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.noFilesDialog);
        return false;
    }

    confirmLegal(): void {
        this.save(true);
        this.loader.toggleLoader(true);
        const save$ = this._requestService.updateScanRequest("Complete Legal", this.requestWorkOrder.id, this.currentRequest).pipe()
            .subscribe(
                (order: WorkOrder) => {
                    this.loader.toggleLoader(false);
                    this.legalCompleteDialog.message = this.legalCompleteDialog.message.replace("[RequestID]", this.index.flowIdentifier);
                    this.legalCompleteDialog.toggleDialog(true);
                    this._dialogService.updateDialog(this.legalCompleteDialog);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.handleError(error);
                }
            );
        this.subscription.add(save$);
    }

    cancelDelete(): void {
        this.pendingDeleteFile = undefined;
    }

    confirmDelete(): void {
        const file = this.pendingDeleteFile;
        this.loader.toggleLoader(true);
        let fileDel$ = this._documentService.abandon(file).pipe(
            tap((deleted) => {
                let index = this.currentRequest.files.findIndex(f => f.blobUri == file.blobUri);
                if (index !== -1) {
                    this.currentRequest.files.splice(index, 1);
                }
                this.filesComponent.removeFile(file);
                this.allowDeactivate.next(this.currentRequest.files == null || this.currentRequest.files.length === 0);
            }),
            mergeMap((deleted) => {  
                this.requestWorkOrder.scanRequest = this.currentRequest;
                return this._workOrderService.save(this.requestWorkOrder);
             }))
            .subscribe(
                (workOrder: WorkOrder) => {
                    this.requestWorkOrder = workOrder;
                    this.currentRequest = workOrder.scanRequest;
                    this.loader.toggleLoader(false);
                    console.log("work order updated");
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.handleError(error);
                }
        );
        this.subscription.add(fileDel$);
    }

    deleteFile(file: IUploadFile): void {
        this.pendingDeleteFile = file;
        this.confirmDeleteDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.confirmDeleteDialog);
    }

    downloadCoversheet(): void {
        this.loader.toggleLoader(true);
        let subs$ = this._documentService.downloadFile(this.originalRequest.coversheetUrl).pipe()
            .subscribe(
                (data: string) => {
                    const link = this.download.nativeElement;
                    link.href = data;
                    link.click();
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.handleError(error);                   
                }
        );
        this.subscription.add(subs$);
    }

    editRequest(): void {
        this.isEditing = true;
        this.allowDeactivate.next(false);
        this.formGroup.enable();
        if (this.currentStep !== ScanningSteps.LegalReview) {
            this.formGroup.get("legalComments").disable();
        }
        if (!this.inScanningGroup) {
            this.formGroup.get("scanningComments").disable();
        }
        this.formGroup.get("rejectReason").disable();
    }

    getDocumentTypes(documentGroup: number): Observable<OnBaseDocumentType[]> {
        let obs$: Observable<OnBaseDocumentType[]>;
        if (this.documentGroupDocumentTypeMap.has(documentGroup)) {
            let types = this.documentGroupDocumentTypeMap.get(documentGroup);
            obs$ = of(types);
        } else {
            obs$ = this._documentService.getDocumentTypesByDocumentGroup(documentGroup).pipe(tap((types: OnBaseDocumentType[]) => {
                this.documentGroupDocumentTypeMap.set(documentGroup, types);
            }));
        }
        return obs$;
    }

    handleError(error: any): void {
        this.errorDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.errorDialog);
    }

    initializeForm(): void {
        if (this.currentRequest) {
            this.formGroup.get("receivedDate").setValue(this.currentRequest.dateDocumentReceived);
            this.formGroup.get("group").setValue(this.currentRequest.groupRequesting);
            this.formGroup.get("requestor").setValue(this.currentRequest.requestor);
            this.formGroup.get("documentGroup").setValue(this.currentDocumentGroup.id);
            this.formGroup.get("documentType").setValue(this.currentDocumentType.id);
            this.formGroup.get("apiNumber").setValue(this.currentRequest.apiNumber);
            this.formGroup.get("apiFound").setValue(this.currentRequest.apiNumber != null && this.currentRequest.apiNumber !== "");
            this.formGroup.get("applicationNumber").setValue(this.currentRequest.applicationNumber);
            this.formGroup.get("applicationNumberFound").setValue(this.currentRequest.applicationNumber != null && this.currentRequest.applicationNumber !== "");
            this.formGroup.get("organizationNumber").setValue(this.currentRequest.organization);
            this.formGroup.get("organizationNumberFound").setValue(this.currentRequest.organization != null && this.currentRequest.organization.length > 0);
            this.formGroup.get("rbdmsNumber").setValue(this.currentRequest.rbdmsNumber);
            this.formGroup.get("rbdmsNumberFound").setValue(this.currentRequest.rbdmsNumber != null && this.currentRequest.rbdmsNumber.length > 0);
            this.formGroup.get("confidential").setValue(this.currentRequest.confidential);
            this.formGroup.get("legalReview").setValue(this.currentRequest.legalReviewRequired);
            this.formGroup.get("pages").setValue(this.currentRequest.pages);
            this.formGroup.get("instructions").setValue(this.currentRequest.instructions);
            this.formGroup.get("scanningComments").setValue(this.currentRequest.scanningComments);
            this.formGroup.get("legalComments").setValue(this.currentRequest.legalComments);
            this.formGroup.get("rejectReason").setValue(this.currentRequest.rejectReason);
        }

        if (this.currentStep === ScanningSteps.LegalReview) {
            this.formGroup.get("legalComments").enable();
        }
        if (this.currentStep === ScanningSteps.InitialScan || this.currentStep === ScanningSteps.ReturnedToScanning) {
            this.formGroup.get("scanningComments").enable();
        }
    }

    invalidateField(fieldName: string): void {
        this.formGroup.get(fieldName).setValue(false);
        switch (fieldName) {
            case "apiFound":
                this.formGroup.get("apiNumber").markAsPristine();
                break;
            case "rbdmsNumberFound":
                this.formGroup.get("rbdmsNumber").markAsPristine();
                break;
            case "organizationNumberFound":
                this.formGroup.get("organizationNumber").markAsPristine();
                break;
            case "applicationNumberFound":
                this.formGroup.get("applicationNumber").markAsPristine();
                break;
        }
        switch (fieldName) {
            case "apiFound":
                this.apiSearchRun = false;
                break;
            case "rbdmsNumberFound":
                this.rbdmsNumberSearchRun = false;
                break;
            case "organizationNumberFound":
                this.organizationSearchRun = false;
                break;
            case "applicationNumberFound":
                this.applicationSearchRun = false;
                break;
        }
    }

    notImplemented(): void {
        this.notImplementedDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.notImplementedDialog);
    }

    onLeaveConfirm(): void {
        this.allowDeactivate.next(true);
    }

    documentGroupChanged(event: Event): void {
        let ctrl = this.formGroup.get("documentGroup");
        if (ctrl) {
            const value = ctrl.value;
            this.documentTypes = [];
            const typeCtrl = this.formGroup.get("documentType");
            if (typeCtrl != null) {
                typeCtrl.setValue(this.selectDocumentType.id);
            }
            let obs$ = this.getDocumentTypes(value).subscribe(
                (types: OnBaseDocumentType[]) => {
                    this.documentTypes = types;
                    if (this.documentTypes.findIndex(d => d.id === -1) === -1) {
                        this.documentTypes.unshift(this.selectDocumentType);
                    }
                },
                (error) => {
                    console.error(error);
                    this.documentTypes = [];
                    this.documentTypes.unshift(this.selectDocumentType);
                }
            );
            this.subscription.add(obs$);

        }
    }

    receivedDateChanged(event: Date): void {
        this.formGroup.get("receivedDate").patchValue(event);
        this.formGroup.updateValueAndValidity();
    }

    rejectScan(): void {
        this.notImplemented();
    }

    requestCompleted(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    save(silent: boolean = false): void {
        const dateReceived = this.formGroup.get("receivedDate").value;
        const instructions = this.formGroup.get("instructions").value;        
        if (<boolean>this.formGroup.get("apiFound").value === true) {
            this.currentRequest.apiNumber = <string>this.formGroup.get("apiNumber").value;
        } else {
            this.formGroup.get("apiNumber").setValue(this.currentRequest.apiNumber);
        }   
        if (<boolean>this.formGroup.get("applicationNumberFound").value === true) {
            this.currentRequest.applicationNumber = <string>this.formGroup.get("applicationNumber").value;
        } else {
            this.formGroup.get("applicationNumber").setValue(this.currentRequest.applicationNumber);
        }  
        this.currentRequest.confidential = <boolean>this.formGroup.get("confidential").value;
        this.currentRequest.dateDocumentReceived = dateReceived;
        const docTypeId = parseInt(this.formGroup.get("documentType").value.toString());
        this.currentRequest.documentType = this.allDocumentTypes.find(t => t.id === docTypeId).name;
        this.currentRequest.groupRequesting = <string>this.formGroup.get("group").value;
        this.currentRequest.instructions = instructions;
        this.currentRequest.legalReviewRequired = <boolean>this.formGroup.get("legalReview").value;
        this.currentRequest.pages = <number>this.formGroup.get("pages").value;
        if (<boolean>this.formGroup.get("organizationNumberFound").value === true) {
            this.currentRequest.organization = <string>this.formGroup.get("organizationNumber").value;            
        } else {
            this.formGroup.get("organizationNumber").setValue(this.currentRequest.organization);            
        }
        if (<boolean>this.formGroup.get("rbdmsNumberFound").value === true) {
            this.currentRequest.rbdmsNumber = <string>this.formGroup.get("rbdmsNumber").value;
        } else {
            this.formGroup.get("rbdmsNumber").setValue(this.currentRequest.rbdmsNumber);
        }
        const docGroupId = parseInt(this.formGroup.get("documentGroup").value.toString());
        const docGroup = this.documentGroups.find(g => g.id === docGroupId);
        this.currentRequest.documentGroup = (docGroup == null) ? this.selectDocumentGroup.name : docGroup.name;
        this.currentRequest.requestor = <string>this.formGroup.get("requestor").value;
        this.currentRequest.legalComments = <string>this.formGroup.get("legalComments").value;
        this.currentRequest.scanningComments = <string>this.formGroup.get("scanningComments").value;
        this.formGroup.disable();
        if (this.currentStep === ScanningSteps.LegalReview) {
            this.formGroup.get("legalComments").enable();
        }
        if (this.currentStep === ScanningSteps.InitialScan || this.currentStep === ScanningSteps.ReturnedToScanning) {
            this.formGroup.get("scanningComments").enable();
        }
        this.isEditing = false;
        if (!silent) {
            this.searchSaveDialog.toggleDialog(true);
            this._dialogService.updateDialog(this.searchSaveDialog);
        }        
        this.allowDeactivate.next(false);
    }

    searchAPI(): void {
        let ctrl = this.formGroup.get("apiNumber");
        if (ctrl && ctrl.value) {
            this.formGroup.get("apiFound").patchValue(false);
            this.loader.toggleLoader(true);
            let apiSearch$ = this._wellService.getWellById(ctrl.value).pipe().subscribe(
                (well) => {
                    this.apiSearchRun = true;
                    this.formGroup.get("apiNumber").markAsDirty();
                    this.formGroup.get("apiFound").setValue(true);
                    this.formGroup.get("apiFound").updateValueAndValidity();
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.apiSearchRun = true;
                    this.formGroup.get("apiNumber").markAsDirty();
                    this.formGroup.get("apiFound").updateValueAndValidity();
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(apiSearch$);
        }
    }

    searchApplications(): void {
        let ctrl = this.formGroup.get("applicationNumber");
        if (ctrl && ctrl.value) {
            this.formGroup.get("applicationNumberFound").setValue(false);
            let spec = new FlowIndexSpec();
            spec.flowIdentifier = ctrl.value;
            this.loader.toggleLoader(true);
            let appSearch$ = this._instanceService.findFlowIndicies(spec).pipe().subscribe(
                (index) => {
                    this.applicationSearchRun = true;
                    this.formGroup.get("applicationNumber").markAsDirty();
                    this.loader.toggleLoader(false);
                    if (index.length > 0) {                        
                        this.formGroup.get("applicationNumberFound").patchValue(true);
                        this.formGroup.get("applicationNumberFound").updateValueAndValidity();                        
                    }                    
                },
                (error) => {
                    console.error(error);
                    this.applicationSearchRun = true;
                    this.formGroup.get("applicationNumber").markAsDirty();
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(appSearch$);
        }
    }

    searchOrganizations(): void {
        let ctrl = this.formGroup.get("organizationNumber");
        if (ctrl && ctrl.value) {
            this.formGroup.get("organizationNumberFound").setValue(false);
            this.formGroup.get("rbdmsNumberFound").setValue(false);
            this.formGroup.get("rbdmsNumber").patchValue("");
            this.organization = null;
            this.loader.toggleLoader(true);
            let obs$ = this._organizationService.getOrganizationStub(ctrl.value).pipe().subscribe(
                (org: OrganizationStub) => {
                    this.organizationSearchRun = true;
                    this.formGroup.get("organizationNumber").markAsDirty();
                    this.formGroup.get("organizationNumberFound").setValue(true);
                    this.formGroup.get("organizationNumberFound").updateValueAndValidity();
                    this.formGroup.get("rbdmsNumber").patchValue((org.historicalOwnerNumber != null) ? org.historicalOwnerNumber : "");
                    this.formGroup.get("rbdmsNumberFound").patchValue(org.historicalOwnerNumber != null && org.historicalOwnerNumber.length > 0);
                    this.organization = org;
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.organizationSearchRun = true;
                    this.formGroup.get("organizationNumber").markAsDirty();
                    this.formGroup.get("organizationNumberFound").updateValueAndValidity();
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(obs$);
        }
    }

    searchRBDMS(): void {
        let ctrl = this.formGroup.get("rbdmsNumber");
        if (ctrl && ctrl.value) {
            this.formGroup.get("rbdmsNumberFound").setValue(false);
            this.formGroup.get("organizationNumberFound").patchValue(false);
            this.formGroup.get("organizationNumber").patchValue("");
            this.organization = null;
            this.loader.toggleLoader(false);
            const obs$ = this._organizationService.getOrganizationStubs([ctrl.value]).pipe().subscribe(
                (orgs: OrganizationStub[]) => {
                    this.rbdmsNumberSearchRun = true;
                    if (orgs.length === 1) {
                        this.organization = orgs[0];
                    } else {
                        let registered = orgs.find(o => o.hasRegistered === true);
                        if (registered) {
                            this.organization = registered;
                        } else {
                            this.organization = orgs[0];
                        }
                    }
                    if (this.organization != null) {
                        this.formGroup.get("rbdmsNumber").markAsDirty();
                        this.formGroup.get("rbdmsNumberFound").patchValue(true);
                        this.formGroup.get("rbdmsNumberFound").updateValueAndValidity();
                        this.formGroup.get("organizationNumber").patchValue(this.organization.id);
                        this.formGroup.get("organizationNumberFound").patchValue(true);
                        this.formGroup.get("organizationNumberFound").updateValueAndValidity();
                    }
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.rbdmsNumberSearchRun = true;
                    this.formGroup.get("rbdmsNumber").markAsDirty();
                    this.formGroup.get("rbdmsNumberFound").updateValueAndValidity();
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(obs$);
        }
    }

    sendToLegal(): void {
        if (this.confirmFiles()) {
            this.save(true);
            this.loader.toggleLoader(true);
            let save$ = this._requestService.updateScanRequest("Legal", this.requestWorkOrder.id, this.currentRequest).pipe()
                .subscribe(
                    (order: WorkOrder) => {
                        this.loader.toggleLoader(false);
                        this.sentToLegalDialog.message = this.sentToLegalDialog.message.replace("[RequestID]", this.index.flowIdentifier);
                        this.sentToLegalDialog.toggleDialog(true);
                        this._dialogService.updateDialog(this.sentToLegalDialog);
                    },
                    (error) => {
                        console.error(error);
                        this.loader.toggleLoader(false);
                        this.handleError(error);
                    }
                );
            this.subscription.add(save$);
        }
    }

    workOrderCompleted(): void {
        this.alreadyCompleteDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.alreadyCompleteDialog);
    }
}

